import { toast } from 'sonner';
import type { PharmacySale } from '../services/httpServices/pharmacySaleService';
import { pharmacySaleService } from '../services/httpServices/pharmacySaleService';
import { getSavedPrinterShareName, getReceiptPrinterHost } from './printerShare';
import { isRemoteHost } from './devices';
import { notifyPrintAgentOffline } from './printAgentOffline';
import { agentRawPrint, probePrintAgent, type AgentHealth } from './printAgent';
import { emitPrintRequest } from './printSocket';

/**
 * Print a pharmacy counter-sale receipt.
 *
 * A receipt printer that lives on another PC is delivered through the backend's
 * real-time print socket: the backend queues the ESC/POS job and wakes the
 * enrolled print agent on that PC (or, as a legacy fallback, a dashboard open
 * there), which prints it. If no receiver is connected, the job is queued and
 * the user is told how to bring the PC back online.
 *
 * A receipt printer on this PC is rendered on the backend and handed to the
 * local print agent, which sends the bytes straight to the workstation's shared
 * 80mm thermal printer. This bypasses the browser's print pipeline entirely
 * (which thermal receipt drivers such as the Rongta 3" often cannot rasterize)
 * and supports automatic cutting.
 *
 * If a printer share name has been saved on this device (via Settings →
 * Devices), it is sent along and overrides the central database value, so each
 * workstation can point at its own printer share.
 */
export async function printPharmacySaleReceipt(sale: PharmacySale): Promise<void> {
  if (!sale?.id) return;

  const shareName = getSavedPrinterShareName();
  const host = getReceiptPrinterHost();

  // Probe the agent first so we can tell "no agent on this PC" (fall back to an
  // on-prem backend) apart from "agent present but rejected the request", and to
  // learn this PC's hostname (used to decide whether the receipt printer is local
  // or on another PC).
  let agentHealth: AgentHealth | null = null;
  let agentRejection: any = null;
  try {
    agentHealth = await probePrintAgent(1200, true);
  } catch (err) {
    agentRejection = err;
  }

  // A receipt printer that lives on another PC is sent straight to its Windows
  // share over SMB from THIS PC's agent (`\\other-pc\share`), so the printer's
  // PC needs no agent or dashboard of its own. This is the normal deployment
  // here: only client workstations run the agent. If the direct send fails
  // (share unreachable / not allowed), fall back to the cloud relay, which
  // requires an agent on the printer's own PC.
  if (isRemoteHost(host, agentHealth?.hostname || '', agentHealth?.ipAddresses || [])) {
    let directError: any = null;

    if (agentHealth) {
      try {
        const rendered = await pharmacySaleService.renderSaleReceipt(
          sale.id,
          shareName || undefined,
          host,
        );
        await agentRawPrint(
          rendered.data.base64,
          rendered.data.shareName || shareName,
          host,
        );
        toast.success(`Receipt sent to ${host}`);
        return;
      } catch (err) {
        directError = err;
      }
    }

    try {
      const relay = await emitPrintRequest({
        shareName,
        host,
        source: 'pharmacy.sale.receipt',
        jobRef: sale.id,
      });
      if (relay?.ok) {
        if (relay.delivered === false) {
          notifyPrintAgentOffline({
            host,
            shareName: shareName || undefined,
            printerName: shareName || undefined,
          });
        } else {
          toast.success(`Receipt sent to ${host}`);
        }
        return;
      }
    } catch {
      /* fall through to the legacy REST relay below */
    }

    try {
      const res = await pharmacySaleService.printSale(sale.id, shareName || undefined, host);
      if (res.data?.delivered === false) {
        // No receiver (agent or dashboard) is connected on the printer's PC.
        notifyPrintAgentOffline({
          host,
          shareName: shareName || undefined,
          printerName: shareName || undefined,
        });
      } else {
        toast.success(`Receipt sent to ${host}`);
      }
    } catch (err: any) {
      toast.error(
        directError?.message || err?.message || 'Could not queue the receipt for printing.',
      );
    }
    return;
  }

  // The local agent is installed but rejected the request (e.g. missing token).
  // Surface that instead of falling back to the cloud backend, which cannot
  // reach a printer on this LAN.
  if (agentRejection) {
    toast.error(agentRejection?.message || 'The local print agent rejected the request');
    return;
  }

  // Local printer: render on the backend, print locally through the agent. Pass
  // no host so the agent resolves this PC's own printer by share/name and skips
  // its remote-printer allow-list (an empty/loopback host is treated as local).
  if (agentHealth) {
    try {
      const rendered = await pharmacySaleService.renderSaleReceipt(
        sale.id,
        shareName || undefined,
        undefined,
      );
      await agentRawPrint(
        rendered.data.base64,
        rendered.data.shareName || shareName,
        undefined,
      );
      toast.success('Receipt sent to printer');
    } catch (err: any) {
      // The agent is running, so it owns the print path. Show the real reason
      // (e.g. remote printing disabled, printer not allowed, share unreachable)
      // instead of falling back to the server, whose cloud `cmd.exe` error is
      // misleading and cannot print to a workstation's printer.
      toast.error(err?.message || 'Failed to print the receipt');
    }
    return;
  }

  // No agent detected on this PC. Only an on-prem backend (running on the
  // printer's machine) can print server-side; on the cloud deployment this
  // cannot work, so make the likely cause — the missing agent — explicit.
  try {
    await pharmacySaleService.printSale(sale.id, shareName || undefined, host || undefined);
    toast.success('Receipt queued — it prints on the printer’s PC.');
  } catch (err: any) {
    const detail = err?.message || 'The server could not print the receipt.';
    // When the backend cannot print server-side (cloud/Linux), the only real fix
    // is the local agent — show that instead of the low-level cmd.exe detail.
    if (/not available on this (deployment|host)/i.test(detail)) {
      toast.error(
        'No Fast Aid Print Agent is running on this PC, so the receipt was not printed. ' +
          'Install and start the agent on this workstation, then try again.',
      );
    } else {
      toast.error(`No print agent was detected on this PC. ${detail}`);
    }
  }
}
